import { apiRecognitions } from "@/api/recognition.api.ts";
import { employeeRecognitionsKeys } from "@/queries/employees/use-employee-recognitions.query.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiRecognition,
  ApiRecognitionCategory,
  ApiRecognitionCategoryPayload,
  ApiRecognitionHistoryFilter,
  ApiRecognitionMassGiftPayload,
  ApiRecognitionMassGiftPreviewFilter,
  ApiRecognitionPayload,
} from "@/types/recognitions/recognition.types.ts";
import { RuleGroupShort } from "@/types/rule.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

export const recognitionsKeys = createQueryKeys("recognitions", {
  categories: null,
  category: (id: number) => [{ id }],
  list: (categoryId?: number) => [{ categoryId }] as const,
  giftPreview: (recipientRuleGroups: RuleGroupShort[]) => [{ recipientRuleGroups }] as const,
  history: (filter: ApiRecognitionHistoryFilter) => [{ filter }] as const,
});

export interface RecognitionCategorySaveParams {
  id?: number;
  payload: ApiRecognitionCategoryPayload;
}

export interface RecognitionSaveParams {
  id?: number;
  payload: ApiRecognitionPayload;
}

interface RecognitionsSortParams {
  categoryId: number;
  ids: number[];
}

export const useRecognitionGiftPreview = (
  recipientRuleGroups: RuleGroupShort[],
  enabled: boolean
) => {
  return useQuery({
    ...recognitionsKeys.giftPreview(recipientRuleGroups),
    enabled: enabled,
    queryFn: async () => {
      const filter: ApiRecognitionMassGiftPreviewFilter = {
        recipient_rule_groups: recipientRuleGroups,
      };
      const result = await apiRecognitions.previewGift(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useRecognitionHistory = (
  filter: ApiRecognitionHistoryFilter,
  enabled: boolean = true
) => {
  return useQuery({
    ...recognitionsKeys.history(filter),
    enabled: enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      const result = await apiRecognitions.historySearch(filter);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useRecognitionCategories = (enabled: boolean = true) => {
  return useQuery({
    ...recognitionsKeys.categories,
    enabled: enabled,
    queryFn: async () => {
      const result = await apiRecognitions.getCategories();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useRecognitionCategory = (id: number | undefined) => {
  return useQuery({
    ...recognitionsKeys.category(id ?? 0),
    enabled: id !== undefined,
    queryFn: async () => {
      const result = await apiRecognitions.getCategory(id!);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useRecognitions = (categoryId?: number, enabled: boolean = true) => {
  return useQuery({
    ...recognitionsKeys.list(categoryId),
    enabled: enabled,
    queryFn: async () => {
      const result = await apiRecognitions.getRecognitions(categoryId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useRecognitionCategorySave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: RecognitionCategorySaveParams) => {
      const result = id
        ? await apiRecognitions.updateCategory(id, payload)
        : await apiRecognitions.createCategory(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.categories.queryKey });
      if (id !== undefined) {
        void queryClient.invalidateQueries({
          queryKey: recognitionsKeys.category(id).queryKey,
        });
      }
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useRecognitionSave = <TFieldValues extends FieldValues = FieldValues>(
  control: Control<TFieldValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: RecognitionSaveParams) => {
      const result = id
        ? await apiRecognitions.updateRecognition(id, payload)
        : await apiRecognitions.createRecognition(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.categories.queryKey });
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useRecognitionGift = <TFieldValues extends FieldValues = FieldValues>(
  control: Control<TFieldValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (payload: ApiRecognitionMassGiftPayload) => {
      const result = await apiRecognitions.giftRecognition(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: recognitionsKeys.list._def }),
        queryClient.invalidateQueries({ queryKey: recognitionsKeys.categories.queryKey }),
        queryClient.invalidateQueries({ queryKey: employeeRecognitionsKeys.search._def }),
        queryClient.invalidateQueries({ queryKey: recognitionsKeys.history._def }),
      ]);
    },

    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useRecognitionDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiRecognitions.deleteRecognition(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.list._def });
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.categories.queryKey });
    },

    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useRecognitionCategoryDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiRecognitions.deleteCategory(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },

    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.categories.queryKey });
    },

    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useRecognitionsSort = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ ids }: RecognitionsSortParams) => {
      const result = await apiRecognitions.sortRecognitions(ids);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onMutate: async ({ categoryId, ids }: RecognitionsSortParams) => {
      const queryKey = recognitionsKeys.list(categoryId).queryKey;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ApiRecognition[]>(queryKey);
      queryClient.setQueryData(queryKey, (old: ApiRecognition[] = []) => {
        const byId = new Map(old.map((item) => [item.id, item]));
        return ids
          .map((id) => byId.get(id))
          .filter((item): item is ApiRecognition => item !== undefined);
      });
      return { queryKey, previous };
    },

    onError: (error: Error, _params, context) => {
      if (context) queryClient.setQueryData(context.queryKey, context.previous);
      onError(error);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.list._def });
    },
  });
};

export const useRecognitionCategoriesSort = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      const result = await apiRecognitions.sortCategories(ids);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onMutate: async (ids: number[]) => {
      const queryKey = recognitionsKeys.categories.queryKey;
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<ApiRecognitionCategory[]>(queryKey);
      queryClient.setQueryData(queryKey, (old: ApiRecognitionCategory[] = []) => {
        const byId = new Map(old.map((item) => [item.id, item]));
        return ids
          .map((id) => byId.get(id))
          .filter((item): item is ApiRecognitionCategory => item !== undefined);
      });
      return { previous };
    },

    onError: (error: Error, _ids, context) => {
      if (context) {
        queryClient.setQueryData(recognitionsKeys.categories.queryKey, context.previous);
      }
      onError(error);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: recognitionsKeys.categories.queryKey });
    },
  });
};
